import type { ReactNode } from 'react';
import type { EstadoCaso, Prioridad, Semaforo as TSemaforo, TipoCaso } from '../api/tipos';
import { claseEstado, ETIQUETA_ESTADO, ETIQUETA_PRIORIDAD, ETIQUETA_TIPO } from '../lib/etiquetas';
import { Icono, type NombreIcono } from './Iconos';

const TEXTO_SEMAFORO: Record<TSemaforo, string> = { VERDE: 'Verde', AMARILLO: 'Amarillo', ROJO: 'Rojo' };

/** Semáforo con punto y texto. Con `texto=""` queda solo el punto (con nombre accesible). */
export function Semaforo({ valor, texto, compacto }: { valor?: TSemaforo | null; texto?: string; compacto?: boolean }) {
  if (!valor) return <span className="texto-tenue">·</span>;
  return (
    <span
      className={`chip chip--${valor.toLowerCase()}${compacto ? ' chip--compacto' : ''}${texto === '' ? ' chip--punto' : ''}`}
      title={`Semáforo ${TEXTO_SEMAFORO[valor].toLowerCase()}`}
      aria-label={texto === '' ? `Semáforo ${TEXTO_SEMAFORO[valor].toLowerCase()}` : undefined}
    >
      <span className={`chip__punto chip__punto--${valor.toLowerCase()}`} aria-hidden="true" />
      {texto ?? TEXTO_SEMAFORO[valor]}
    </span>
  );
}

/** Punto de semáforo con forma distinta por color (círculo, rombo, cuadrado): el color no es la única señal. */
export function PuntoSemaforo({ valor, titulo }: { valor?: TSemaforo | null; titulo?: string }) {
  if (!valor) return null;
  const t = titulo ?? `Semáforo ${TEXTO_SEMAFORO[valor].toLowerCase()}`;
  return <span className={`punto-semaforo punto-semaforo--${valor.toLowerCase()}`} role="img" aria-label={t} title={t} />;
}

export function ChipEstado({ estado, grande }: { estado: EstadoCaso; grande?: boolean }) {
  return <span className={`lozenge lozenge--${claseEstado(estado)} ${grande ? 'lozenge--grande' : ''}`}>{ETIQUETA_ESTADO[estado] ?? estado}</span>;
}

const ICONO_PRIORIDAD: Record<Prioridad, NombreIcono> = { alta: 'prioridadAlta', media: 'prioridadMedia', baja: 'prioridadBaja' };

export function IconoPrioridad({ prioridad, tam = 16 }: { prioridad: Prioridad; tam?: 16 | 20 }) {
  return (
    <span className={`icono-prioridad icono-prioridad--${prioridad}`} title={`Prioridad ${ETIQUETA_PRIORIDAD[prioridad].toLowerCase()}`}>
      <Icono nombre={ICONO_PRIORIDAD[prioridad]} tam={tam} titulo={`Prioridad ${ETIQUETA_PRIORIDAD[prioridad].toLowerCase()}`} />
    </span>
  );
}

export function ChipPrioridad({ prioridad }: { prioridad: Prioridad }) {
  return (
    <span className="prioridad">
      <IconoPrioridad prioridad={prioridad} />
      {ETIQUETA_PRIORIDAD[prioridad]}
    </span>
  );
}

const ICONO_TIPO: Record<TipoCaso, NombreIcono> = {
  por_clasificar: 'tipoPorClasificar',
  suministro: 'tipoSuministro',
  servicio: 'tipoServicio',
  proyecto: 'tipoProyecto',
  licitacion: 'tipoLicitacion',
};

/** Baldosa de color con el glifo del tipo de caso. */
export function IconoTipo({ tipo, tam = 16 }: { tipo: TipoCaso; tam?: 16 | 20 }) {
  const t = ETIQUETA_TIPO[tipo] ?? tipo;
  return (
    <span className={`icono-tipo icono-tipo--${tipo} icono-tipo--${tam}`} title={`Tipo: ${t}`} role="img" aria-label={`Tipo: ${t}`}>
      <Icono nombre={ICONO_TIPO[tipo] ?? 'tipoPorClasificar'} tam={tam === 20 ? 16 : 12} />
    </span>
  );
}

export function ChipEtiqueta({ texto, alQuitar }: { texto: string; alQuitar?: () => void }) {
  return (
    <span className="chip-etiqueta">
      {texto}
      {alQuitar && (
        <button type="button" className="chip-etiqueta__quitar" onClick={alQuitar} aria-label={`Quitar etiqueta ${texto}`}>
          <Icono nombre="cerrar" tam={16} />
        </button>
      )}
    </span>
  );
}

export function Etiqueta({ children, tono = 'neutro' }: { children: ReactNode; tono?: 'neutro' | 'azul' | 'alerta' | 'peligro' | 'exito' }) {
  return <span className={`etiqueta etiqueta--${tono}`}>{children}</span>;
}

/** Píldora de progreso con el puntaje de salud del negocio (0..100). */
export function PildoraSalud({ puntaje }: { puntaje: number | null }) {
  if (puntaje === null) return <span className="pildora pildora--vacia">Salud sin evaluar</span>;
  const p = Math.max(0, Math.min(100, Math.round(puntaje)));
  return (
    <span className="pildora" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p} aria-label={`Salud del negocio ${p} %`} title={`Salud del negocio ${p} %`}>
      <span className="pildora__barra" style={{ width: `${Math.max(p, 12)}%` }}>
        <span className="pildora__texto">{p} %</span>
      </span>
    </span>
  );
}

